import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import { CopyInputField } from "../../../common/components/forms/inputs/InputField/CopyInputField";
import { copyToClipboard } from "../../../common/utilities/clipboardUtility";
import { getSuccessToast } from "../../../common/infrastructure/utilities/toastUtility";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import styles from "../Authenticate.module.scss";
import { usePermissions } from "../../../common/infrastructure/hooks/usePermissions";
import { IUserModel } from "../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../AppContext";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { getDefaultRoutePath } from "../../../common/infrastructure/routes/routePaths";
import { getCurrentUserRole } from "../../../common/infrastructure/helpers/userHelper";
import { UserRole } from "../../../common/enums";

export const useAuthenticationFunctions = () => {
  const { mutate: mutate } = useApiMutation();
  const { currentUser } = useAppContext();
  const navigate = useNavigate();

  const { resetStudios } = useAppContext();
  const signIn = useSignIn();

  const { userHavePermissions } = usePermissions();

  const userHasPermissionsToGeneratePasswordResetLink = useMemo(
    () =>
      userHavePermissions(
        endpointsMetadata.authentication.generatePasswordResetLink,
      ),
    [currentUser],
  );

  const generateAndCopyResetLinkConfirmMessage = (
    <div className={styles.confirm}>
      <p className={styles.confirm_first_p}>
        Сигурни ли сте, че желаете да генерирате линк за
        <strong> възстановяване на парола</strong> за този потребител?
      </p>

      <p>
        С тази операция ще бъде дадено съгласие{" "}
        <strong>от името на потребителя</strong> със следните условия,{" "}
        <strong>в случай че все още не е дадено</strong>:
      </p>

      <ul>
        <li>Да получава автоматизирани имейли от платформата Planny</li>
        <li>Че приема Политиката за поверителност на Planny</li>
        <li>
          Че е съгласен с Общите условия за използване на обекта (ако такива
          съществуват)
        </li>
      </ul>

      <p>
        Това е{" "}
        <strong>
          задължителна стъпка при опит за създаване на парола за потребителски
          профил
        </strong>{" "}
        в платформата.
      </p>

      <p>
        <strong>
          Продължете само ако имате изричното съгласие на потребителя за това.
        </strong>
      </p>
    </div>
  );

  const linkGeneratedSuccessfully = (link: string) => (
    <div className={styles.confirm}>
      Линкът за възстановяване на паролата беше генериран <b>успешно</b>. Можете
      да си го копирате от тук:
      <CopyInputField
        className={styles.confirm_input}
        value={link}
        width="100%"
        label="Линк"
      />
    </div>
  );

  const onTryGenerateAndCopyPasswordResetLink = (id: number) => {
    showCustomConfirmDialog(
      generateAndCopyResetLinkConfirmMessage,
      () =>
        mutate(
          {
            endpoint: `${endpointsMetadata.authentication.generatePasswordResetLink}?userId=${id}`,
            globalIsLoading: true,
            method: "post",
            invalidateKeys: [[endpointsMetadata.user.getUser, id]],
            disableSuccessToast: true,
          },
          {
            async onSuccess(data) {
              const link = data as string;
              const success = await copyToClipboard(link);

              if (success) {
                getSuccessToast("Линкът е копиран в клипборда!");
              } else {
                showCustomConfirmDialog(
                  linkGeneratedSuccessfully(link),
                  undefined,
                  () => {},
                  "success",
                  "Добре",
                  null,
                );
              }
            },
          },
        ),
      () => {},
      "warning",
      "Да",
      "Не",
    );
  };

  /**
   * Handles successful user login by storing auth state and redirecting.
   */
  const onSuccessfulUserLogin = async (
    currentUser: IUserModel,
    returnUrl?: string,
  ): Promise<void> => {
    if (currentUser) {
      const { permissions, ...userWithoutPermissions } = currentUser;

      localStorage.setItem("permissions", JSON.stringify(permissions));

      const signInResult = signIn({
        auth: {
          token: currentUser.authToken,
          type: "Bearer",
        },
        userState: userWithoutPermissions,
      });

      if (signInResult) {
        if (getCurrentUserRole(currentUser) !== UserRole.User) {
          resetStudios();
        }

        // Redirect to appropriate page
        const redirectPath = returnUrl || getDefaultRoutePath(currentUser);
        window.location.href = redirectPath;
      } else {
        console.error("Failed to sign in");
      }
    } else {
      console.error("onSuccessfulUserLogin called with null/undefined user");
    }
  };

  return {
    userHasPermissionsToGeneratePasswordResetLink,
    onSuccessfulUserLogin,
    onTryGenerateAndCopyPasswordResetLink,
  };
};
